import classNames from 'classnames';
import { useCallback, useMemo } from 'react';

import { type GallerySectionAddition } from '@air/component-gallery-view';

import { type GalleryMetadata } from '~/components/Gallery/galleryMetadata';
import LoadMoreSpinner from '~/components/LoadMoreSpinner';

const SECTION_FOOTER_LOADER_HEIGHT = 104;
const SECTION_FOOTER_COLLAPSED_HEIGHT = 16;

export const useGalleryDynamicSectionFooters = (
  sections: {
    loadMore: (() => void) | undefined;
    isLoading: boolean;
    hasMore: boolean;
    containerWidth: number;
    containerPadding: number;
  }[],
): (GallerySectionAddition<GalleryMetadata> | undefined)[] => {
  const loaderBoxes = useMemo(
    () =>
      sections.map((s, index) => ({
        aspectRatio: 0,
        left: s.containerPadding,
        top: 0,
        width: s.containerWidth - 2 * s.containerPadding,
        height: s.hasMore || s.isLoading ? SECTION_FOOTER_LOADER_HEIGHT : SECTION_FOOTER_COLLAPSED_HEIGHT,
        showLoader: s.hasMore || s.isLoading,
        isLastSection: index === sections.length - 1,
      })),
    [sections],
  );

  const renderLoader = useCallback(
    (index: number, isVisible: boolean) => {
      const { loadMore, isLoading } = sections[index];

      return loadMore ? (
        <LoadMoreSpinner key="loader" isVisible={isVisible} isLoading={isLoading} loadMore={loadMore} />
      ) : null;
    },
    [sections],
  );

  return loaderBoxes.map((loaderBox) => ({
    box: loaderBox,
    render: (index, isVisible) => (
      <div
        className={classNames({
          'ads:border-ads-gray-5 border-gray-5 border-b': !loaderBox.isLastSection,
        })}
      >
        {loaderBox.showLoader ? renderLoader(index, isVisible) : null}
      </div>
    ),
  }));
};
